import { isApiError } from '@multaqa/api-client'
import { toast } from '@multaqa/ui/components/ui/sonner'
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query'
import { formatErrorMessage } from './error-message'

/**
 * Resolve an error to a user-friendly toast message.
 */
export function messageFor(error: unknown): string {
  return formatErrorMessage(error)
}

/**
 * A mutation whose form already shows some failures inline sets
 * `inlineError` to the predicate for those failures; the global cache stays
 * quiet for exactly those and toasts everything else.
 */
export type MutationErrorMeta = { inlineError?: (error: unknown) => boolean }

declare module '@tanstack/react-query' {
  interface Register {
    mutationMeta: MutationErrorMeta
  }
}

/**
 * One QueryClient for the app.
 *
 * The global error handlers exist so no feature has to remember to surface a
 * failure. They are deliberately asymmetric:
 *
 *  - **Mutations** always toast. A write that failed silently is the worst
 *    outcome in an ERP — the user believes the invoice was posted.
 *  - **Queries** only toast when there is already data on screen (a background
 *    refetch that failed). A first load renders its own error state, and a
 *    toast on top of that is noise.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Server data is not re-fetched on every mount. 30s is long enough to
        // make navigation feel instant and short enough that a second user's
        // change shows up without a manual refresh.
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => {
          // Retrying a 401/403/404/422 just repeats a definitive answer.
          if (isApiError(error) && error.status >= 400 && error.status < 500) {
            return false
          }

          return failureCount < 2
        },
      },
      mutations: {
        retry: false,
      },
    },

    queryCache: new QueryCache({
      onError: (error, query) => {
        if (query.state.data !== undefined) {
          toast.error(messageFor(error))
        }
      },
    }),

    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        if (mutation.options.meta?.inlineError?.(error)) return
        toast.error(messageFor(error))
      },
    }),
  })
}
